import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronDown, Home, Plus, X } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, matchPath, useLocation, useNavigate } from "react-router-dom";

import { useNavigationPersona } from "@/auth/navigationPersona";
import { usePermission } from "@/auth/permissions/usePermission";
import {
  canAny,
  getFeatureNavigationPermission,
  type FeatureMetadata,
} from "@/features/feature.types";
import { FEATURES } from "@/features/featureRegistry";
import { getBestAccessiblePath } from "@/navigation/bestAccessibleRoute";
import { isFeatureVisibleInNavigation } from "@/navigation/menuConfig";

type WorkspaceTab = { id: string; label: string; href: string };

export function WorkspaceTabs() {
  const location = useLocation();
  const navigate = useNavigate();
  const { can } = usePermission();
  const persona = useNavigationPersona();
  const features = useMemo<FeatureMetadata[]>(
    () =>
      FEATURES.filter(
        (feature) =>
          canAny(can, getFeatureNavigationPermission(feature, persona)) &&
          isFeatureVisibleInNavigation(feature, persona),
      ),
    [can, persona],
  );
  const home = getBestAccessiblePath(can, {
    preferredFeatureIds: ["dashboard"],
    fallbackPath: "/403",
    persona,
  });
  const homeFeature = features.find((entry) => entry.navigation.path === home);
  const homeLabel =
    (persona === "guardian" ? homeFeature?.guardianNavigation?.label : undefined) ??
    homeFeature?.navigation.label ??
    "Home";
  const feature = [...features]
    .sort((a, b) => b.navigation.path.length - a.navigation.path.length)
    .find((entry) =>
      matchPath({ path: entry.navigation.path, end: false }, location.pathname),
    );
  const id = feature?.id ?? location.pathname;
  const label =
    feature?.guardianNavigation?.label && persona === "guardian"
      ? feature.guardianNavigation.label
      : (feature?.navigation.label ??
        (location.pathname === "/notifications" ? "Notifications" : "Workspace"));
  const href = location.pathname + location.search + location.hash;
  const isHome = location.pathname === home;
  const [tabs, setTabs] = useState<WorkspaceTab[]>([]);
  const activeRef = useRef<HTMLAnchorElement>(null);
  const homeRef = useRef<HTMLAnchorElement>(null);
  const tabLinks = useRef(new Map<string, HTMLAnchorElement>());
  const focusAfterClose = useRef<HTMLAnchorElement | null>(null);

  useLayoutEffect(() => {
    focusAfterClose.current?.focus();
    focusAfterClose.current = null;
  }, [tabs]);

  useEffect(() => {
    if (isHome) return;
    setTabs((previous) => {
      const existing = previous.find((tab) => tab.id === id);
      return existing
        ? previous.map((tab) => (tab.id === id ? { id, label, href } : tab))
        : [...previous, { id, label, href }];
    });
  }, [href, id, isHome, label]);

  useEffect(() => {
    activeRef.current?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [href, tabs.length]);
  const visibleTabs = tabs.filter(
    (tab) => tab.id.startsWith("/") || features.some((entry) => entry.id === tab.id),
  );
  const closeTab = (tab: WorkspaceTab) => {
    const index = visibleTabs.findIndex((entry) => entry.id === tab.id);
    const neighbor = visibleTabs[index - 1] ?? visibleTabs[index + 1];
    focusAfterClose.current =
      (neighbor && tabLinks.current.get(neighbor.id)) || homeRef.current;
    setTabs((previous) => previous.filter((entry) => entry.id !== tab.id));
    if (tab.id === id && !isHome)
      navigate(visibleTabs[index - 1]?.href ?? visibleTabs[index + 1]?.href ?? home);
  };

  return (
    <nav
      aria-label="Open workspaces"
      className="flex min-w-0 flex-1 items-center gap-1 self-stretch"
    >
      <Link
        ref={homeRef}
        to={home}
        className={`workspace-tab shrink-0 ${isHome ? "is-active" : ""}`}
        aria-current={isHome ? "page" : undefined}
        title={`${homeLabel} home`}
        aria-label={homeLabel}
      >
        <Home size={15} />
        <span className="hidden sm:inline">{homeLabel}</span>
      </Link>
      <div className="flex min-w-0 items-center gap-1 overflow-x-auto [scrollbar-width:none]">
        {visibleTabs.map((tab) => (
          <div
            key={tab.id}
            className={`workspace-tab shrink-0 !gap-1 !pr-1 ${tab.id === id && !isHome ? "is-active" : ""}`}
          >
            <Link
              ref={(node) => {
                if (node) tabLinks.current.set(tab.id, node);
                else tabLinks.current.delete(tab.id);
                if (tab.id === id) activeRef.current = node;
              }}
              to={tab.href}
              className="max-w-36 truncate py-1"
              aria-current={tab.id === id && !isHome ? "page" : undefined}
              title={tab.label}
            >
              {tab.label}
            </Link>
            <button
              type="button"
              className="shell-icon !h-7 !w-7"
              aria-label={`Close ${tab.label} workspace`}
              onClick={() => closeTab(tab)}
            >
              <X size={13} />
            </button>
          </div>
        ))}
      </div>
      <Menu as="div" className="relative shrink-0">
        <MenuButton className="shell-icon" aria-label="List open workspaces">
          <ChevronDown size={15} />
        </MenuButton>
        <MenuItems className="shell-popover absolute left-0 top-full z-50 mt-1 max-h-80 w-60 overflow-y-auto p-1">
          <MenuItem>
            <Link to={home} className="shell-menu-item">
              {homeLabel}
            </Link>
          </MenuItem>
          {visibleTabs.map((tab) => (
            <MenuItem key={tab.id}>
              <Link to={tab.href} className="shell-menu-item">
                {tab.label}
              </Link>
            </MenuItem>
          ))}
        </MenuItems>
      </Menu>
      <button
        type="button"
        className="shell-icon hidden shrink-0 sm:inline-flex"
        aria-label="Open a module in a workspace"
        title="Open module (Ctrl/Cmd + K)"
        onClick={() => window.dispatchEvent(new Event("sms:open-search"))}
      >
        <Plus size={15} />
      </button>
    </nav>
  );
}
